'use client';

// Two contexts: state (changes often) and dispatch (stable). Components that
// only dispatch never re-render on state changes.

import { createContext, useContext, useEffect, useReducer, useCallback, useRef, type Dispatch, type ReactNode } from 'react';
import {
  editorReducer, createInitialState, isViewOnlyEditorAction, normalizeEditorDoc, analysisFromResults,
  premiumFeatureOf,
  type EditorState, type EditorAction, type EditorDoc, type IterationState, type PremiumFeature,
} from './editorState';

const StateCtx = createContext<EditorState | null>(null);
const DispatchCtx = createContext<Dispatch<EditorAction> | null>(null);

/**
 * `initialDoc`, when provided (a saved room server-fetched by the page),
 * seeds the editor document so the room renders immediately on mount — no
 * client-side load round-trip. It is shallow-merged over the defaults so a
 * doc saved by an older schema still gets any newer top-level field.
 * `initialResults`, when present, seeds freq/rt60/soundstage analysis directly.
 */
export function EditorProvider({
  children,
  initialDoc,
  initialResults,
  initialIterations,
  readOnly = false,
  basicRoom = false,
  onPremiumAction,
}: {
  children: ReactNode;
  initialDoc?: EditorDoc;
  initialResults?: Record<string, unknown>;
  initialIterations?: IterationState;
  /** Public share — blocks document mutations at dispatch. */
  readOnly?: boolean;
  /** A basic room (ADR 014) — blocks every action that adds a panel at
   *  dispatch, whatever triggered it: a button, Ctrl+D, a paste from another
   *  room, the mirror-pair button. One gate here, so no new way of adding one
   *  can forget it. */
  basicRoom?: boolean;
  /** Called instead of the blocked action, so the room can offer premium. */
  onPremiumAction?: (feature: PremiumFeature) => void;
}) {
  const [state, dispatch] = useReducer(editorReducer, undefined, () => {
    const base = createInitialState();
    if (initialIterations && initialIterations.items.length > 0) {
      return editorReducer(base, {
        type: 'HYDRATE',
        doc: initialDoc ? normalizeEditorDoc(initialDoc) : base.doc,
        results: initialResults,
        iterations: initialIterations,
      });
    }
    const stateWithDoc = initialDoc ? { ...base, doc: normalizeEditorDoc(initialDoc) } : base;
    if (initialResults) {
      // Was hand-rolled here — freq/rt60/soundstage got seeded directly
      // without ever clearing beforeStale/afterStale (which default to
      // true), unlike the multi-Design HYDRATE path above, which already
      // goes through analysisFromResults. Route through the same function
      // so both hydration paths agree on what "a fresh result" means.
      const seeded = analysisFromResults(initialResults);
      if (seeded.freq || seeded.rt60 || seeded.soundstage) {
        return {
          ...stateWithDoc,
          analysis: { ...stateWithDoc.analysis, ...seeded },
        };
      }
    }
    return stateWithDoc;
  });

  // The tier gate is read through a ref so dispatch keeps one identity when a
  // room turns premium mid-session: every dispatch consumer relies on it being
  // stable (see the header).
  const tierGateRef = useRef({ basicRoom, onPremiumAction });
  useEffect(() => {
    tierGateRef.current = { basicRoom, onPremiumAction };
  }, [basicRoom, onPremiumAction]);

  const guardedDispatch = useCallback<Dispatch<EditorAction>>((action) => {
    if (readOnly && !isViewOnlyEditorAction(action.type)) return;
    const gate = tierGateRef.current;
    if (gate.basicRoom) {
      const feature = premiumFeatureOf(action.type);
      if (feature) {
        gate.onPremiumAction?.(feature);
        return;
      }
    }
    dispatch(action);
  }, [readOnly]);

  return (
    <DispatchCtx.Provider value={guardedDispatch}>
      <StateCtx.Provider value={state}>{children}</StateCtx.Provider>
    </DispatchCtx.Provider>
  );
}

export function useEditor(): EditorState {
  const s = useContext(StateCtx);
  if (!s) throw new Error('useEditor must be used within <EditorProvider>');
  return s;
}

export function useDispatch(): Dispatch<EditorAction> {
  const d = useContext(DispatchCtx);
  if (!d) throw new Error('useDispatch must be used within <EditorProvider>');
  return d;
}
